/**
 * ConnectionBanner — global top-of-viewport banner that surfaces backend
 * connectivity status. Reads `connectionState` from AuthContext:
 *
 *   "healthy"       → render nothing (no UI noise when all is well)
 *   "reconnecting"  → amber banner "Reconnecting…" while the interceptor is
 *                     mid-retry on a 5xx/520-524 response
 *   "down"          → red banner "Connection issue" after retries exhausted;
 *                     the user can manually retry by reloading
 *
 * The actual retry logic lives in AuthContext's response interceptor — this
 * component is presentation-only.
 */
import React from "react";
import { useAuth } from "../AuthContext";
import { RefreshCw, AlertTriangle } from "lucide-react";

export default function ConnectionBanner() {
  const { connectionState } = useAuth() || {};

  if (!connectionState || connectionState === "healthy") return null;

  const isReconnecting = connectionState === "reconnecting";
  const bg = isReconnecting
    ? "linear-gradient(90deg,#b45309 0%,#d97706 100%)"  // amber
    : "linear-gradient(90deg,#991b1b 0%,#b91c1c 100%)"; // red

  return (
    <div
      data-testid="connection-banner"
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-2 px-4 py-1.5 text-[12px] font-semibold text-white"
      style={{ background: bg, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
    >
      {isReconnecting ? (
        <>
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          <span>Reconnecting…</span>
          <span className="opacity-75 hidden sm:inline">— retrying the last request</span>
        </>
      ) : (
        <>
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>Connection issue — retry in a moment</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="connection-banner-reload"
            className="ml-2 px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 transition-colors"
          >
            Reload
          </button>
        </>
      )}
    </div>
  );
}
